import * as React from 'react';
import {
    Button,
    DialogTitle,
    DialogActions,
    Dialog,
    DialogContent,
    Autocomplete,
    TextField,
    Typography,
    Box,
    Stack,
    IconButton,
    Chip,
    Divider,
    CircularProgress,
    MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDefaultDashboardWidgetJsonSettings } from '../../utilities/DashboardUtilities';
import TaskApiService from '../../api/TaskApiService';
import LocationApiService from '../../api/LocationApiService';

const taskApiService = new TaskApiService();
const locationApiService = new LocationApiService();
let cachedTaskStatuses = null;
let taskStatusesRequest = null;

const userAssignedTasksComponent = 'DashboardUserAssignedTasks';
const taskStatusComponent = 'DashboardTaskStatus';
const legacyTaskSummaryComponent = 'DashboardTaskSummary';
const taskDueDateSummaryComponent = 'DashboardTaskDueDateSummary';
const riskLevelComponent = 'DashboardRiskLevel';
const riskByDepartmentComponent = 'DashboardRiskByDepartment';
const energyPerformanceComponent = 'DashboardEnergyPerformanceSummary';
const riskFilterTypes = {
    none: '',
    location: 'location'
};
const riskByDepartmentGroupTypes = {
    department: 'department',
    location: 'location'
};

async function getCachedTaskStatuses() {
    if (cachedTaskStatuses) {
        return cachedTaskStatuses;
    }

    if (!taskStatusesRequest) {
        taskStatusesRequest = taskApiService.getTaskStatuses().then((statuses) => {
            cachedTaskStatuses = statuses;
            return statuses;
        });
    }

    return taskStatusesRequest;
}

function parseJsonSettings(jsonSettings) {
    try {
        if (!jsonSettings || jsonSettings === '{}') {
            return {};
        }

        return typeof jsonSettings === 'string' ? JSON.parse(jsonSettings) : jsonSettings;
    }
    catch (error) {
        console.error('Error parsing jsonSettings:', error);
        return {};
    }
}

function getExcludedStatusIdsFromJsonSettings(component, jsonSettings) {
    const settings = parseJsonSettings(jsonSettings || getDefaultDashboardWidgetJsonSettings(component));

    if (component !== userAssignedTasksComponent && component !== taskDueDateSummaryComponent && component !== taskStatusComponent && component !== legacyTaskSummaryComponent) {
        return [];
    }

    if (!Object.prototype.hasOwnProperty.call(settings, 'excludedStatusIds')) {
        return parseJsonSettings(getDefaultDashboardWidgetJsonSettings(component)).excludedStatusIds ?? [];
    }

    return Array.isArray(settings.excludedStatusIds) ? settings.excludedStatusIds : [];
}

function getRiskLevelFilterSettings(jsonSettings) {
    const settings = parseJsonSettings(jsonSettings);
    const selectedLocationName = settings.filterType === riskFilterTypes.location || settings.selectedLocationName
        ? settings.selectedLocationName ?? ''
        : '';

    return {
        selectedLocationName,
        baselineDate: settings.startDate ?? '',
        comparisonDate: settings.endDate ?? ''
    };
}

function getRiskByDepartmentSettings(jsonSettings) {
    const settings = parseJsonSettings(jsonSettings);

    return {
        groupBy: settings.groupBy === riskByDepartmentGroupTypes.location
            ? riskByDepartmentGroupTypes.location
            : riskByDepartmentGroupTypes.department
    };
}

export default function DashboardWidgetModal(props) {
    const { open, position, onClose, onSave, setSnackbarProperties, widgetToEdit } = props;
    const { t } = useTranslation();
    const [selectedComponent, setSelectedComponent] = useState(undefined);
    const [name, setName] = useState(undefined);
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [taskStatusesLoading, setTaskStatusesLoading] = useState(false);
    const [excludedStatusIds, setExcludedStatusIds] = useState([]);
    const [selectedRiskLocationName, setSelectedRiskLocationName] = useState('');
    const [riskBaselineDate, setRiskBaselineDate] = useState('');
    const [riskComparisonDate, setRiskComparisonDate] = useState('');
    const [riskByDepartmentGroupBy, setRiskByDepartmentGroupBy] = useState(riskByDepartmentGroupTypes.department);
    const [riskLocations, setRiskLocations] = useState([]);
    const [riskFilterOptionsLoading, setRiskFilterOptionsLoading] = useState(false);

    const dashboardComponentOptions = useMemo(() => [
        { name: t('dashboardWidget.component.taskStatus'), component: taskStatusComponent, userCreated: false },
        { name: t('dashboardWidget.component.userAssignedTasks'), component: 'DashboardUserAssignedTasks', userCreated: false },
        { name: t('dashboardWidget.component.riskLevel'), component: riskLevelComponent, userCreated: false },
        { name: t('dashboardWidget.component.riskByDepartment'), component: riskByDepartmentComponent, userCreated: false },
        { name: t('dashboardWidget.component.energyPerformance'), component: energyPerformanceComponent, userCreated: false },
        { name: t('dashboardWidget.component.taskDueDateSummary'), component: 'DashboardTaskDueDateSummary', userCreated: false },
    ], [t]);

    const selectedExcludedStatuses = taskStatuses.filter((status) => excludedStatusIds.includes(status.stId));
    const riskLocationOptions = useMemo(() => [...new Set(riskLocations
        .map((location) => location.pjName)
        .filter((locationName) => locationName && locationName.trim()))]
        .sort((a, b) => a.localeCompare(b)), [riskLocations]);

    function handleSave() {
        if (!selectedComponent) {
            setSnackbarProperties(t('dashboardWidget.validation.componentRequired'), 'error');
            return;
        }

        if (!name) {
            setSnackbarProperties(t('dashboardWidget.validation.nameRequired'), 'error');
            return;
        }

        if (selectedComponent.component === riskLevelComponent
            && ((riskBaselineDate && !riskComparisonDate) || (!riskBaselineDate && riskComparisonDate))) {
            setSnackbarProperties(t('dashboardRiskLevel.validation.snapshotDatePair'), 'error');
            return;
        }

        const jsonSettings = getJsonSettingsForSelectedComponent();

        let dashboardComponent;
        if (widgetToEdit) {
            dashboardComponent = {
                ...widgetToEdit,
                position: position,
                name: name,
                component: selectedComponent.component,
                jsonSettings: jsonSettings,
            };
        }
        else if (!selectedComponent.userCreated) {
            const componentId = position + 1;
            dashboardComponent = {
                id: `box${componentId}`,
                position: position,
                name: name,
                component: selectedComponent.component,
                jsonSettings: jsonSettings,
            };
        }
        else {
            dashboardComponent ={
                ...selectedComponent,
                position: position,
                jsonSettings: jsonSettings,
            }
        }

        onSave(dashboardComponent);
    }

    function getJsonSettingsForSelectedComponent() {
        const existingJsonSettings = widgetToEdit?.component === selectedComponent.component
            ? widgetToEdit.jsonSettings || getDefaultDashboardWidgetJsonSettings(selectedComponent.component)
            : getDefaultDashboardWidgetJsonSettings(selectedComponent.component);
        const settings = parseJsonSettings(existingJsonSettings);

        if (selectedComponent.component === userAssignedTasksComponent
            || selectedComponent.component === taskDueDateSummaryComponent
            || selectedComponent.component === taskStatusComponent
            || selectedComponent.component === legacyTaskSummaryComponent) {
            return JSON.stringify({
                ...settings,
                excludedStatusIds: excludedStatusIds,
            });
        }

        if (selectedComponent.component === riskLevelComponent) {
            const locationOnlySettings = { ...settings };
            delete locationOnlySettings.selectedProjectId;
            delete locationOnlySettings.projectFilterEnabled;

            return JSON.stringify({
                ...locationOnlySettings,
                filterType: selectedRiskLocationName ? riskFilterTypes.location : riskFilterTypes.none,
                selectedLocationName: selectedRiskLocationName,
                startDate: riskBaselineDate,
                endDate: riskComparisonDate
            });
        }

        if (selectedComponent.component === riskByDepartmentComponent) {
            return JSON.stringify({
                ...settings,
                groupBy: riskByDepartmentGroupBy,
            });
        }

        return existingJsonSettings;
    }

    function handleSelectedComponentChange(newValue) {
        setSelectedComponent(newValue);
        setName(newValue?.name);
        setExcludedStatusIds(getExcludedStatusIdsFromJsonSettings(newValue?.component, getDefaultDashboardWidgetJsonSettings(newValue?.component)));
        const riskSettings = getRiskLevelFilterSettings(getDefaultDashboardWidgetJsonSettings(newValue?.component));
        setSelectedRiskLocationName(riskSettings.selectedLocationName);
        setRiskBaselineDate(riskSettings.baselineDate);
        setRiskComparisonDate(riskSettings.comparisonDate);
        const riskByDepartmentSettings = getRiskByDepartmentSettings(getDefaultDashboardWidgetJsonSettings(newValue?.component));
        setRiskByDepartmentGroupBy(riskByDepartmentSettings.groupBy);
    }

    useEffect(() => {
        if (open) {
            if (widgetToEdit) {
                const matchedComponent = dashboardComponentOptions.find(
                    (option) => option.component === widgetToEdit.component
                );

                setName(widgetToEdit.name);
                setSelectedComponent(matchedComponent);
                setExcludedStatusIds(getExcludedStatusIdsFromJsonSettings(widgetToEdit.component, widgetToEdit.jsonSettings));
                const riskSettings = getRiskLevelFilterSettings(widgetToEdit.jsonSettings);
                setSelectedRiskLocationName(riskSettings.selectedLocationName);
                setRiskBaselineDate(riskSettings.baselineDate);
                setRiskComparisonDate(riskSettings.comparisonDate);
                const riskByDepartmentSettings = getRiskByDepartmentSettings(widgetToEdit.jsonSettings);
                setRiskByDepartmentGroupBy(riskByDepartmentSettings.groupBy);
                return;
            }

            setName(undefined);
            setSelectedComponent(undefined);
            setExcludedStatusIds([]);
            setSelectedRiskLocationName('');
            setRiskBaselineDate('');
            setRiskComparisonDate('');
            setRiskByDepartmentGroupBy(riskByDepartmentGroupTypes.department);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, widgetToEdit]);

    useEffect(() => {
        if (!open
            || (selectedComponent?.component !== userAssignedTasksComponent
                && selectedComponent?.component !== taskDueDateSummaryComponent
                && selectedComponent?.component !== taskStatusComponent
                && selectedComponent?.component !== legacyTaskSummaryComponent)) {
            return;
        }

        let ignore = false;
        setTaskStatusesLoading(true);

        getCachedTaskStatuses()
            .then((statuses) => {
                if (!ignore) {
                    setTaskStatuses(statuses);
                }
            })
            .finally(() => {
                if (!ignore) {
                    setTaskStatusesLoading(false);
                }
            });

        return () => {
            ignore = true;
        };
    }, [open, selectedComponent]);

    useEffect(() => {
        if (!open || selectedComponent?.component !== riskLevelComponent) {
            return;
        }

        let ignore = false;
        setRiskFilterOptionsLoading(true);

        locationApiService.getFullLocationsForCurrentOrg()
            .then((locationResponse) => {
                if (!ignore) {
                    setRiskLocations(locationResponse.data ?? []);
                }
            })
            .finally(() => {
                if (!ignore) {
                    setRiskFilterOptionsLoading(false);
                }
            });

        return () => {
            ignore = true;
        };
    }, [open, selectedComponent]);

    useEffect(() => {
        if (!selectedRiskLocationName || riskFilterOptionsLoading) {
            return;
        }

        if (!riskLocationOptions.includes(selectedRiskLocationName)) {
            setSelectedRiskLocationName('');
        }
    }, [riskFilterOptionsLoading, riskLocationOptions, selectedRiskLocationName]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                elevation: 8,
                sx: {
                    borderRadius: 3,
                    minHeight: '500px'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DashboardCustomizeIcon sx={{ fontSize: 28, color: '#99C20E' }} />
                        <Box>
                            <Typography variant="h5" fontWeight="600">
                                {widgetToEdit ? t('dashboardWidget.title.edit') : t('dashboardWidget.title.create')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {widgetToEdit ? t('dashboardWidget.subtitle.edit') : t('dashboardWidget.subtitle.create')}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ py: 3 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                            {t('dashboardWidget.section.selectType')}
                        </Typography>
                        <Autocomplete
                            options={dashboardComponentOptions}
                            value={selectedComponent || null}
                            onChange={(event, newValue) => {
                                handleSelectedComponentChange(newValue);
                            }}
                            getOptionLabel={(option) => option?.name || t('dashboardWidget.option.unknown')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('dashboardWidget.field.chooseWidget')}
                                    variant="outlined"
                                    placeholder={t('dashboardWidget.field.searchWidgets')}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" fontWeight="500">
                                                {option.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.component}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={option.userCreated ? t('dashboardWidget.option.custom') : t('dashboardWidget.option.default')}
                                            size="small"
                                            color={option.userCreated ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                    </Stack>
                                </Box>
                            )}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                            {t('dashboardWidget.section.name')}
                        </Typography>
                        <TextField
                            value={name || ''}
                            label={t('dashboardWidget.field.enterName')}
                            onChange={(event) => setName(event.target.value)}
                            fullWidth
                            variant="outlined"
                            placeholder={t('dashboardWidget.field.namePlaceholder')}
                            helperText={t('dashboardWidget.field.nameHelper')}
                        />
                    </Box>

                    {(selectedComponent?.component === userAssignedTasksComponent
                        || selectedComponent?.component === taskDueDateSummaryComponent
                        || selectedComponent?.component === taskStatusComponent
                        || selectedComponent?.component === legacyTaskSummaryComponent) && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                                {t('dashboardWidget.section.excludedStatuses')}
                            </Typography>
                            <Autocomplete
                                multiple
                                options={taskStatuses}
                                value={selectedExcludedStatuses}
                                loading={taskStatusesLoading}
                                onChange={(event, newValue) => {
                                    setExcludedStatusIds(newValue.map((status) => status.stId));
                                }}
                                getOptionLabel={(option) => option?.localizedName ?? option?.stName ?? ''}
                                isOptionEqualToValue={(option, value) => option.stId === value.stId}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('dashboardWidget.field.excludedStatusesLabel')}
                                        variant="outlined"
                                        placeholder={t('dashboardWidget.field.excludedStatusesPlaceholder')}
                                        helperText={t('dashboardWidget.field.excludedStatusesHelper')}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {taskStatusesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {selectedComponent?.component === riskLevelComponent && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                                {t('dashboardRiskLevel.filter.locationSection')}
                            </Typography>
                            <Stack spacing={2}>
                                <Autocomplete
                                    options={riskLocationOptions}
                                    value={selectedRiskLocationName || null}
                                    loading={riskFilterOptionsLoading}
                                    onChange={(event, newValue) => {
                                        setSelectedRiskLocationName(newValue ?? '');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('dashboardRiskLevel.filter.location')}
                                            size="small"
                                        />
                                    )}
                                />
                            </Stack>

                            <Typography variant="subtitle1" fontWeight="600" sx={{ mt: 3, mb: 2 }}>
                                {t('dashboardRiskLevel.filter.dateSection')}
                            </Typography>
                            <Stack spacing={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        label={t('dashboardRiskLevel.filter.baselineDate')}
                                        type="date"
                                        size="small"
                                        value={riskBaselineDate}
                                        onChange={(event) => setRiskBaselineDate(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                    <TextField
                                        label={t('dashboardRiskLevel.filter.comparisonDate')}
                                        type="date"
                                        size="small"
                                        value={riskComparisonDate}
                                        onChange={(event) => setRiskComparisonDate(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                    )}

                    {selectedComponent?.component === riskByDepartmentComponent && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                                {t('dashboardRiskByDept.groupBy')}
                            </Typography>
                            <TextField
                                select
                                value={riskByDepartmentGroupBy}
                                label={t('dashboardRiskByDept.groupBy')}
                                onChange={(event) => setRiskByDepartmentGroupBy(event.target.value)}
                                fullWidth
                                variant="outlined"
                                helperText={t('dashboardRiskByDept.groupByHelper')}
                            >
                                <MenuItem value={riskByDepartmentGroupTypes.department}>
                                    {t('dashboardRiskByDept.group.department')}
                                </MenuItem>
                                <MenuItem value={riskByDepartmentGroupTypes.location}>
                                    {t('dashboardRiskByDept.group.location')}
                                </MenuItem>
                            </TextField>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                    sx={{ minWidth: 100 }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        minWidth: 120,
                        backgroundColor: '#99C20E',
                        '&:hover': { backgroundColor: '#76960c' }
                    }}
                    disabled={!selectedComponent || !name}
                >
                    {widgetToEdit ? t('dashboardWidget.button.saveEdit') : t('dashboardWidget.button.add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
